import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { EMPTY, Observable, from } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { OeAppService } from './oe-app.service';
import { ProfileService } from './profile.service';
import { generateSha256 } from './sha256.util';
import {
  parseOsFamily,
  parseOsVersion,
  toMajorMinor,
  detectIsTablet,
  detectNotch,
  detectFontScale,
} from './device-fingerprint.helpers';

export interface SsoSessionRequest {
  ssoTokenHash: string;
  ssoToken: string;
}

export interface SessionResponse {
  session_id: string;
  app_store_url: string;
}

@Injectable({
  providedIn: 'root',
})
export class DeviceFingerprintService {
  private sessionCreated = false;

  constructor(private http: HttpClient, private oeAppService: OeAppService, private profileService: ProfileService) {}

  createSession(ssoToken: string): Observable<SessionResponse> {
    if (this.sessionCreated) {
      return EMPTY;
    }

    const endpoint = `${this.oeAppService.ssoServiceHost}/v1/sso-sessions`;

    return from(this.getDeviceFingerprint()).pipe(
      switchMap((ssoTokenHash) =>
        this.http.post<SessionResponse>(endpoint, { ssoTokenHash, ssoToken } as SsoSessionRequest, {
          // Need to send the ssoToken in the headers manually
          // because it has to be the same as the one in the body(refresh-token)
          // otherwise the sso-sessions endpoint will return a 401 Unauthorized error
          headers: new HttpHeaders().set('Authorization', `Bearer ${ssoToken}`),
        })
      ),
      tap(() => {
        this.sessionCreated = true;
      })
    );
  }

  async getSignals(): Promise<string[]> {
    const ua = navigator.userAgent;

    const ipv4 = await this.profileService.getIpAddress().toPromise();
    const osFamily = parseOsFamily(ua);
    const osVersion = toMajorMinor(await parseOsVersion(ua));
    const screenWidth = String(screen.width);
    const screenHeight = String(screen.height);
    const pixelRatio = String(Math.round(window.devicePixelRatio));
    const language = navigator.language.split('-')[0];
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const fontScale = detectFontScale().toFixed(1);
    const hasNotch = String(detectNotch());
    const isTablet = String(detectIsTablet(ua));

    return [
      ipv4,
      osFamily,
      osVersion,
      screenWidth,
      screenHeight,
      pixelRatio,
      language,
      timezone,
      fontScale,
      hasNotch,
      isTablet,
    ];
  }

  async getDeviceFingerprint(): Promise<string> {
    const signals = await this.getSignals();
    if (signals.some((s) => !s)) {
      throw new Error('One or more device fingerprint signals are empty');
    }

    const input = signals.join('|');
    const hash = await generateSha256(input);
    return hash;
  }
}



// WEBPACK FOOTER //
// ./ts/app/shared/device-fingerprint.service.ts